import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface DownloadCardsPdfButtonProps {
  cardWidthMm: number;
  cardHeightMm: number;
  filenameSlug: string;
}

const BATCH_SIZE = 40;

async function captureCardPngDataUrl(node: HTMLElement): Promise<string> {
  return toPng(node, { cacheBust: true, pixelRatio: 3 });
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function DownloadCardsPdfButton({
  cardWidthMm,
  cardHeightMm,
  filenameSlug,
}: DownloadCardsPdfButtonProps) {
  const [startBatch, setStartBatch] = useState(3);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
    batch: number;
    batchTotal: number;
  } | null>(null);

  const handleDownload = async () => {
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>('[data-pdf-page]'),
    );
    if (nodes.length === 0) {
      alert('No cards found.');
      return;
    }

    const batches = chunk(nodes, BATCH_SIZE);
    const startIndex = Math.min(
      Math.max(startBatch - 1, 0),
      batches.length - 1,
    );
    const doneAlready = startIndex * BATCH_SIZE;
    setProgress({
      done: doneAlready,
      total: nodes.length,
      batch: startIndex,
      batchTotal: batches.length,
    });

    try {
      let doneCount = doneAlready;
      for (let b = startIndex; b < batches.length; b++) {
        const batchNodes = batches[b];
        const pdf = new jsPDF({
          orientation: cardWidthMm > cardHeightMm ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [cardWidthMm, cardHeightMm],
          compress: true,
        });

        for (let i = 0; i < batchNodes.length; i++) {
          const dataUrl = await captureCardPngDataUrl(batchNodes[i]);
          if (i > 0) pdf.addPage([cardWidthMm, cardHeightMm]);
          pdf.addImage(
            dataUrl,
            'PNG',
            0,
            0,
            cardWidthMm,
            cardHeightMm,
            undefined,
            'FAST',
          );
          doneCount++;
          setProgress({
            done: doneCount,
            total: nodes.length,
            batch: b + 1,
            batchTotal: batches.length,
          });
        }

        pdf.save(
          `credenciales-${filenameSlug}-part-${String(b + 1).padStart(2, '0')}-of-${String(batches.length).padStart(2, '0')}.pdf`,
        );
        // Give the browser a beat between downloads to avoid being blocked.
        await sleep(500);
      }
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('PDF generation failed. Check console.');
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor="pdf-start-batch"
        className="flex items-center gap-1 text-gray-600 text-sm"
      >
        Start at batch
        <Input
          id="pdf-start-batch"
          type="number"
          min={1}
          value={startBatch}
          onChange={(e) => setStartBatch(Number(e.target.value) || 1)}
          disabled={progress !== null}
          className="w-16"
        />
      </label>
      <Button onClick={handleDownload} disabled={progress !== null}>
        {progress
          ? `Batch ${progress.batch}/${progress.batchTotal} — ${progress.done}/${progress.total} cards…`
          : `Download PDF (batches of ${BATCH_SIZE})`}
      </Button>
    </div>
  );
}
